import { Component, Output, EventEmitter, HostListener, ElementRef, ChangeDetectorRef, OnInit, Inject, PLATFORM_ID } from '@angular/core';
import { Router } from '@angular/router';
import { isPlatformBrowser } from '@angular/common';
import { MessageService } from 'primeng/api';
import { TranslateService } from '@ngx-translate/core';
import { EmpresaConfigService } from '../../services/configuracoes/empresa.service';
import { PermissaoService } from '../../services/permissao/permissao.service';
import { ModuloPermissaoDashboard } from '../../models/dashboard/modulo-permissao.model';

/** Estrutura de um sub-item do menu */
interface MenuItem {
  key: string;
  icon: string;
  labelKey: string;
  route?: string;
  children?: MenuItem[];
  /** Secao de agrupamento (exibida como titulo) */
  sectionTitle?: string;
  /** Separador visual simples (linha horizontal), sem titulo */
  separator?: boolean;
  /** Link externo (abre em nova aba) */
  externalLink?: string;
  /** Controla visibilidade condicional do item */
  condicional?: boolean;
  /** Visivel apenas para usuarios com categoriaAcessoMaster */
  masterOnly?: boolean;
  /** Visivel apenas para usuarios contadores */
  contadorOnly?: boolean;
  /** Visivel apenas para quem NAO e contador (itens do lado CLIENTE) */
  naoContadorOnly?: boolean;
  /** Campo de permissao necessario para exibir este item */
  permissao?: keyof ModuloPermissaoDashboard;
  /** Alternativa ao `permissao`: exibe o item quando o usuario tem QUALQUER uma delas */
  permissoes?: (keyof ModuloPermissaoDashboard)[];
  /** Acao customizada ao clicar (em vez de navegar por rota) */
  action?: () => void;
  /** Se true, TODOS os filhos deste item ficam sempre expandidos (nao colapsaveis) */
  todosFilhosSempreExpandidos?: boolean;
}

@Component({
  selector: 'app-menu-lateral',
  templateUrl: './menu-lateral.component.html',
  styleUrl: './menu-lateral.component.scss',
})
export class MenuLateralComponent implements OnInit {
  @Output() sidebarStateChange = new EventEmitter<boolean>();

  /** Item principal atualmente com flyout aberto */
  activeItem: string = '';

  /** Sub-item selecionado (para highlight) */
  selectedItem: string = '';

  /** Sub-item expandido no flyout (para sub-submenus) */
  expandedSubItem: string = '';

  /** Chaves de sub-itens que devem permanecer SEMPRE expandidos (nao permitem recolher) */
  private readonly subItensSempreExpandidos: string[] = ['registros'];

  /** Posicao top do flyout (calculada dinamicamente) */
  flyoutTop: number = 0;

  /** Indica se o usuario logado tem acesso master */
  acessoMaster: boolean = false;

  /** Indica se o usuario logado e contador */
  acessoContador: boolean = false;

  /** Indica se a empresa tem o Mine Shop habilitado */
  minishopHabilitado: boolean = false;

  /** Itens principais do menu */
  menuItems: MenuItem[] = [
    {
      key: 'dashboard',
      icon: 'pi pi-th-large',
      labelKey: 'menu-lateral.texts.dashboard',
      route: '/dashboard',
      permissao: 'dashboard',
    },
    {
      key: 'cli',
      icon: 'pi pi-address-book',
      labelKey: 'menu-lateral.texts.clientes',
      children: [
        { key: 'clientes', icon: 'pi pi-users', labelKey: 'menu-lateral.lists.clientes', route: '/clientes', permissao: 'cliente' },
        { key: 'fornecedores', icon: 'pi pi-id-card', labelKey: 'menu-lateral.lists.fornecedores', route: '/fornecedores', permissao: 'fornecedor' },
      ]
    },
    {
      key: 'vendas',
      icon: 'pi pi-shopping-cart',
      labelKey: 'menu-lateral.texts.vendas',
      permissao: 'pontoDeVenda',
      children: [
        { key: 'nova-venda', icon: 'pi pi-cart-plus', labelKey: 'menu-lateral.lists.nova_venda', route: '/vendas', permissao: 'pontoDeVenda' },
        { key: 'vendas-list', icon: 'pi pi-list-check', labelKey: 'menu-lateral.lists.lista_de_vendas', route: '/vendas-list', permissao: 'pontoDeVenda' },
        { key: 'devolucao-vendas', icon: 'pi pi-arrow-right-arrow-left', labelKey: 'menu-lateral.lists.devolucao_de_vendas', route: '/devolucao-vendas', permissao: 'pontoDeVenda' },
        { key: 'section-mine-shop-vendas', icon: '', labelKey: 'menu-lateral.lists.mine_shop', sectionTitle: 'menu-lateral.lists.mine_shop' },
        { key: 'pedidos', icon: 'pi pi-clipboard', labelKey: 'menu-lateral.lists.pedidos', route: '/pedidos', permissao: 'visualizarPedidos' },
        { key: 'criar-pedido', icon: 'pi pi-shopping-bag', labelKey: 'menu-lateral.lists.criar_pedido', route: '/criar-pedido', permissao: 'gestaoDePedidos' },
        { key: 'delivery', icon: 'pi pi-truck', labelKey: 'menu-lateral.lists.delivery', permissao: 'pontoDeVenda', action: () => this.abrirDelivery() },
        { key: 'section-mine-shop-config', icon: '', labelKey: '', separator: true },
        { key: 'configuracoes-mine-shop-atalho', icon: 'pi pi-shopping-bag', labelKey: 'menu-lateral.lists.config_mine_shop', route: '/configuracoes-mine-shop', permissao: 'configuracaoMinishop' },
      ]
    },
    {
      key: 'produtos',
      icon: 'pi pi-shopping-bag',
      labelKey: 'menu-lateral.texts.configuracao_de_produtos',
      permissao: 'produto',
      children: [
        { key: 'produtos', icon: 'pi pi-box', labelKey: 'menu-lateral.lists.produtos', route: '/produtos', permissao: 'produto' },
        { key: 'section-registros', icon: '', labelKey: 'menu-lateral.lists.registros', sectionTitle: 'menu-lateral.lists.registros' },
        { key: 'grupos', icon: 'pi pi-folder-open', labelKey: 'menu-lateral.lists.grupos', route: '/grupo', permissao: 'grupo' },
        { key: 'subgrupo', icon: 'pi pi-file-o', labelKey: 'menu-lateral.lists.subgrupos', route: '/subgrupo', permissao: 'subgrupo' },
        { key: 'marca', icon: 'pi pi-tag', labelKey: 'menu-lateral.texts.marca', route: '/marca', permissao: 'marca' },
        { key: 'unidade-de-medida', icon: 'pi pi-calculator', labelKey: 'menu-lateral.texts.unidade_de_medida', route: '/unidade-de-medida', permissao: 'unidade' },
        { key: 'section-compras', icon: '', labelKey: 'menu-lateral.texts.compras', sectionTitle: 'menu-lateral.texts.compras' },
        { key: 'adicionar-compra', icon: 'pi pi-cart-plus', labelKey: 'menu-lateral.lists.adicionar_compra', route: '/adicionar-compra', permissao: 'compra' },
        { key: 'compras-list', icon: 'pi pi-shopping-cart', labelKey: 'menu-lateral.lists.lista_de_compras', route: '/compras-list', permissao: 'compra' },
        { key: 'devolucao-compras', icon: 'pi pi-cart-minus', labelKey: 'menu-lateral.lists.devolucao_de_compras', route: '/devolucao-compras', permissao: 'compra' },
      ]
    },
    {
      key: 'accMgmt',
      icon: 'pi pi-wallet',
      labelKey: 'menu-lateral.texts.financeiro',
      children: [
        // Visao geral do dinheiro: entra solta no topo, como "Itens" no menu de Itens.
        { key: 'lista-de-movimentos', icon: 'pi pi-sync', labelKey: 'menu-lateral.lists.extrato_de_caixa', route: '/lista-de-movimentos', permissoes: ['extratoConta', 'relatorioExtratoDeCaixa'] },
        { key: 'section-lancamentos', icon: '', labelKey: 'menu-lateral.lists.lancamentos', sectionTitle: 'menu-lateral.lists.lancamentos' },
        { key: 'nova-despesa', icon: 'pi pi-wallet', labelKey: 'menu-lateral.lists.nova_despesa', route: '/nova-despesa', permissao: 'gastos' },
        { key: 'nova-receita', icon: 'pi pi-dollar', labelKey: 'menu-lateral.lists.nova_receita', route: '/nova-receita', permissao: 'receitas' },
        { key: 'nova-transferencia', icon: 'pi pi-arrow-right-arrow-left', labelKey: 'menu-lateral.lists.nova_transferencia', route: '/nova-transferencia', permissao: 'transferencias' },
        { key: 'section-titulos', icon: '', labelKey: 'menu-lateral.lists.contas_pagar_receber', sectionTitle: 'menu-lateral.lists.contas_pagar_receber' },
        { key: 'contas-a-receber', icon: 'pi pi-dollar', labelKey: 'menu-lateral.lists.contas_a_receber', route: '/contas-pagar-receber?tipo=ENTRADA', permissao: 'contasAReceber' },
        { key: 'contas-a-pagar', icon: 'pi pi-wallet', labelKey: 'menu-lateral.lists.contas_a_pagar', route: '/contas-pagar-receber?tipo=SAIDA', permissao: 'contasAPagar' },
        { key: 'section-financeiro-cadastros', icon: '', labelKey: 'menu-lateral.lists.registros', sectionTitle: 'menu-lateral.lists.registros' },
        { key: 'contas', icon: 'pi pi-building-columns', labelKey: 'menu-lateral.lists.contas', route: '/contas', permissao: 'conta' },
        { key: 'cotacao', icon: 'pi pi-chart-line', labelKey: 'menu-lateral.lists.cotacao', route: '/cotacao', permissao: 'configuracao' },
      ]
    },
    {
      key: 'indicacoes',
      icon: 'pi pi-share-alt',
      labelKey: 'menu-lateral.lists.indicacoes',
      route: '/indicacoes',
      permissao: 'indicacoes',
      // Exige o privilegio "Indicacoes" (espelha o gating do mobile).
    },
    {
      key: 'contador',
      icon: 'pi pi-briefcase',
      labelKey: 'menu-lateral.texts.contador',
      // Grupo "Contador" (flyout, como o Financeiro): Área do Contador (a própria
      // página decide solicitar/dashboard) + Operar Cliente (só contador). Os dois
      // exigem o privilegio "Gestao do Contador". Item-pai sem permissao: visível
      // se ao menos 1 filho for visível (ver isItemVisivel), então o grupo inteiro
      // some para quem não tem o privilégio.
      children: [
        {
          key: 'area-contador',
          icon: 'pi pi-briefcase',
          labelKey: 'menu-lateral.lists.area_contador',
          route: '/contador/area',
          permissao: 'gestaoContador',
        },
        {
          key: 'operar-cliente',
          icon: 'pi pi-user-edit',
          labelKey: 'menu-lateral.lists.operar_cliente',
          route: '/contador/operar',
          contadorOnly: true,
          permissao: 'gestaoContador',
          // So faz sentido para contador (com clientes vinculados) — coerente com o
          // contadorAccessGuard da rota /contador/operar.
        },
      ],
    },
    {
      key: 'meu-contador',
      icon: 'pi pi-bell',
      labelKey: 'menu-lateral.lists.meu_contador',
      route: '/contador/notificacoes',
      naoContadorOnly: true,
      permissao: 'gestaoContador',
      // Exige o privilegio "Gestao do Contador" (espelha o gating do mobile).
      // Acesso permanente do CLIENTE ao relacionamento com o contador: contrato
      // ativo + solicitacoes de contrato + despesas a aprovar. Fica FORA do grupo
      // "Contador" (é a visão do CLIENTE, não da empresa contadora) — coerente com
      // o naoContadorAccessGuard da rota /contador/notificacoes.
    },
    {
      key: 'relatorios',
      icon: 'pi pi-chart-bar',
      labelKey: 'menu-lateral.texts.relatorios',
      route: '/relatorios',
      // Uma janela por relatorio: o item aparece para quem tem qualquer uma.
      permissoes: [
        'relatorioVendas', 'relatorioItensVendidos', 'relatorioListaItens',
        'relatorioExtratoDeCaixa', 'relatorioContasPagas', 'relatorioContasAPagar',
        'relatorioContasRecebidas', 'relatorioContasAReceber', 'relatorioDre',
      ],
    },
  ];

  /** Itens fixos no rodape da sidebar */
  footerMenuItems: MenuItem[] = [
    {
      key: 'area-master',
      icon: 'pi pi-shield',
      labelKey: 'menu-lateral.lists.area_master',
      masterOnly: true,
      // Agrupa TODAS as telas de acesso master (antes espalhadas em "Configuracoes").
      // `masterOnly` no pai ja esconde o grupo inteiro para quem nao e master; os
      // filhos repetem a marca para continuarem protegidos se forem reaproveitados.
      children: [
        { key: 'section-master-suporte', icon: '', labelKey: 'menu-lateral.lists.suporte_ao_cliente', sectionTitle: 'menu-lateral.lists.suporte_ao_cliente' },
        { key: 'master-empresas', icon: 'pi pi-building', labelKey: 'menu-lateral.lists.master_empresas', route: '/master/empresas', masterOnly: true },
        { key: 'master-usuarios', icon: 'pi pi-users', labelKey: 'menu-lateral.lists.master_usuarios', route: '/master/usuarios', masterOnly: true },
        { key: 'master-planos', icon: 'pi pi-chart-line', labelKey: 'menu-lateral.lists.master_planos', route: '/master/planos', masterOnly: true },
        { key: 'configuracao-empresas', icon: 'pi pi-building-columns', labelKey: 'menu-lateral.lists.lista_de_empresas', route: '/configuracao-empresas', permissao: 'configuracao', masterOnly: true },
        { key: 'section-master-cadastros', icon: '', labelKey: 'menu-lateral.lists.registros', sectionTitle: 'menu-lateral.lists.registros' },
        { key: 'configuracao-estado', icon: 'pi pi-map', labelKey: 'menu-lateral.lists.estado', route: '/configuracao-estado', permissao: 'configuracao', masterOnly: true },
        { key: 'configuracao-cidade', icon: 'pi pi-map-marker', labelKey: 'menu-lateral.lists.cidade', route: '/configuracao-cidade', permissao: 'configuracao', masterOnly: true },
        { key: 'configuracao-atividade-economica', icon: 'pi pi-dollar', labelKey: 'menu-lateral.lists.atividade_economica', route: '/configuracao-atividade-economica', permissao: 'configuracao', masterOnly: true },
        { key: 'configuracao-unidade-de-medida', icon: 'pi pi-wrench', labelKey: 'menu-lateral.lists.unidade_de_medida', route: '/configuracao-unidade-de-medida', permissao: 'configuracao', masterOnly: true },
        { key: 'section-master-indicacoes', icon: '', labelKey: 'menu-lateral.lists.indicacoes_e_comissoes', sectionTitle: 'menu-lateral.lists.indicacoes_e_comissoes' },
        { key: 'master-cupons', icon: 'pi pi-ticket', labelKey: 'menu-lateral.lists.master_cupons', route: '/master/cupons', masterOnly: true },
        { key: 'comissoes', icon: 'pi pi-dollar', labelKey: 'menu-lateral.lists.comissoes', route: '/contador/comissoes', masterOnly: true },
        { key: 'section-master-contador', icon: '', labelKey: 'menu-lateral.texts.contador', sectionTitle: 'menu-lateral.texts.contador' },
        { key: 'solicitacao-contador', icon: 'pi pi-user-plus', labelKey: 'menu-lateral.lists.solicitacao_contador', route: '/contador/aprovacao', masterOnly: true },
        { key: 'section-master-sistema', icon: '', labelKey: 'menu-lateral.lists.sistema', sectionTitle: 'menu-lateral.lists.sistema' },
        { key: 'configuracao-geral', icon: 'pi pi-cog', labelKey: 'menu-lateral.lists.configuracao_geral', route: '/configuracao-geral', permissao: 'configuracao', masterOnly: true },
        { key: 'avisos', icon: 'pi pi-megaphone', labelKey: 'menu-lateral.lists.avisos', route: '/avisos', masterOnly: true },
      ],
    },
    {
      key: 'planos',
      icon: 'pi pi-crown',
      labelKey: 'menu-lateral.texts.planos',
      route: '/planos',
      permissao: 'planos',
    },
    {
      key: 'config',
      icon: 'pi pi-cog',
      labelKey: 'menu-lateral.texts.configuracoes',
      // Sem permissao propria: cada filho tem a sua (configuracao, timbrado,
      // dadosDeCobranca, impressoras, privilegios...) e `isItemVisivel` exibe o
      // pai quando ao menos um filho aparece.
      children: [
        { key: 'section-gerais', icon: '', labelKey: 'menu-lateral.lists.config_gerais', sectionTitle: 'menu-lateral.lists.config_gerais' },
        { key: 'configuracoes-da-empresa', icon: 'pi pi-building', labelKey: 'Empresa', route: '/configuracoes-da-empresa', permissao: 'configuracao' },
        { key: 'dados-de-recebimento', icon: 'pi pi-qrcode', labelKey: 'menu-lateral.texts.dados_de_recebimento', route: '/dados-de-recebimento', permissao: 'dadosDeCobranca' },
        { key: 'configuracoes-mine-shop', icon: 'pi pi-shopping-bag', labelKey: 'menu-lateral.lists.config_mine_shop', route: '/configuracoes-mine-shop', permissao: 'configuracaoMinishop' },
        { key: 'timbrado', icon: 'pi pi-file', labelKey: 'menu-lateral.lists.timbrado', route: '/timbrado', permissao: 'timbrado' },
        { key: 'impressoras', icon: 'pi pi-print', labelKey: 'impressoras.texts.titulo', route: '/impressoras', permissao: 'impressoras' },
        { key: 'section-seguranca', icon: '', labelKey: 'menu-lateral.lists.seguranca', sectionTitle: 'menu-lateral.lists.seguranca' },
        { key: 'adicionar-usuario', icon: 'pi pi-users', labelKey: 'menu-lateral.lists.lista_de_usuarios', route: '/adicionar-usuario', permissao: 'funcionario' },
        { key: 'privilegios-do-usuario', icon: 'pi pi-list-check', labelKey: 'menu-lateral.lists.privilegios', route: '/privilegios-do-usuario', permissao: 'privilegios' },
        { key: 'section-suporte', icon: '', labelKey: 'menu-lateral.lists.suporte', sectionTitle: 'menu-lateral.lists.suporte' },
        { key: 'suporte-whatsapp', icon: 'pi pi-whatsapp', labelKey: 'menu-lateral.lists.suporte_por_whatsapp', externalLink: 'https://wa.me/595993290585' },
      ]
    },
  ];

  constructor(
    private router: Router,
    private elementRef: ElementRef,
    private cdr: ChangeDetectorRef,
    private empresaConfigService: EmpresaConfigService,
    private permissaoService: PermissaoService,
    private messageService: MessageService,
    private translate: TranslateService,
    @Inject(PLATFORM_ID) private platformId: Object,
  ) { }

  ngOnInit(): void {
    this.carregarAcessoMaster();
    this.carregarAcessoContador();
    this.carregarConfigEmpresa();
  }

  /** Verifica se o usuario logado tem categoriaAcessoMaster */
  private carregarAcessoMaster(): void {
    if (isPlatformBrowser(this.platformId)) {
      const valor = sessionStorage.getItem('categoriaAcessoMaster') || localStorage.getItem('categoriaAcessoMaster');
      this.acessoMaster = valor === 'true';
    }
  }

  /** Verifica se o usuario logado e contador */
  private carregarAcessoContador(): void {
    if (isPlatformBrowser(this.platformId)) {
      const valor = sessionStorage.getItem('contador') || localStorage.getItem('contador');
      this.acessoContador = valor === 'true';
    }
  }

  /**
   * Carrega a sessao da empresa: Mine Shop e, sobretudo, se a empresa e um CONTADOR
   * (`configuracaoEmpresa.contador`) — fonte AUTORITATIVA para os itens exclusivos do
   * contador (ex.: "Operar Cliente"). Corrige o flag `contador` do storage (que so e
   * gravado no login e pode ficar obsoleto ao trocar de empresa), mantendo o menu e o
   * guard consistentes com a empresa realmente logada.
   */
  private carregarConfigEmpresa(): void {
    this.empresaConfigService.getSessao().subscribe({
      next: (empresa: any) => {
        this.minishopHabilitado = empresa?.habilitarMiniShopping === true;
        if (typeof empresa?.contador === 'boolean') {
          this.acessoContador = empresa.contador;
          // Sincroniza o flag nos dois storages (o guard le ambos com ||), corrigindo
          // valor obsoleto de um login/empresa anterior.
          if (isPlatformBrowser(this.platformId)) {
            sessionStorage.setItem('contador', String(empresa.contador));
            localStorage.setItem('contador', String(empresa.contador));
          }
        }
        this.cdr.detectChanges();
      },
      error: () => {
        this.minishopHabilitado = false;
      },
    });
  }

  /** Abre o cardapio digital (delivery) em nova aba, consultando o status atual no backend */
  abrirDelivery(): void {
    this.empresaConfigService.getStatusMiniShopping().subscribe({
      next: (status) => {
        // Caso 1: minishop nao habilitado
        if (!status.habilitado) {
          this.messageService.add({
            severity: 'warn',
            summary: this.translate.instant('menu-lateral.delivery.minishop_desabilitado_titulo'),
            detail: this.translate.instant('menu-lateral.delivery.minishop_desabilitado_msg'),
            life: 6000,
          });
          this.router.navigate(['/configuracoes-mine-shop']);
          return;
        }

        // Caso 2: habilitado mas sem URL configurada
        if (!status.url) {
          this.messageService.add({
            severity: 'warn',
            summary: this.translate.instant('menu-lateral.delivery.url_nao_configurada_titulo'),
            detail: this.translate.instant('menu-lateral.delivery.url_nao_configurada_msg'),
            life: 6000,
          });
          this.router.navigate(['/configuracoes-mine-shop']);
          return;
        }

        // Caso 3: tudo configurado — abre o cardapio em nova aba
        window.open(`/menu/${status.url}`, '_blank');
      },
      error: () => {
        this.messageService.add({
          severity: 'error',
          summary: this.translate.instant('erro'),
          detail: this.translate.instant('menu-lateral.delivery.erro_buscar_status'),
          life: 6000,
        });
      },
    });
  }

  /** Fecha o flyout ao clicar fora do menu */
  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent) {
    if (!this.elementRef.nativeElement.contains(event.target)) {
      this.closeFlyout();
    }
  }

  /** Fecha o flyout ao pressionar Escape */
  @HostListener('document:keydown.escape')
  onEscapeKey() {
    this.closeFlyout();
  }

  /** Retorna o item de menu ativo (para o flyout) */
  getActiveMenuItem(): MenuItem | undefined {
    return this.menuItems.find(item => item.key === this.activeItem)
      || this.footerMenuItems.find(item => item.key === this.activeItem);
  }

  /** Abre/fecha o flyout de um item principal */
  toggleFlyout(item: MenuItem, event: MouseEvent) {
    // Se o item tem rota direta (sem filhos), navegar
    if (item.route && !item.children) {
      this.selectedItem = item.key;
      this.activeItem = '';
      this.expandedSubItem = '';
      this.router.navigateByUrl(item.route);
      return;
    }

    // Toggle do flyout
    if (this.activeItem === item.key) {
      this.closeFlyout();
    } else {
      const el = (event.currentTarget as HTMLElement);
      const rect = el.getBoundingClientRect();
      this.flyoutTop = rect.top;
      this.activeItem = item.key;
      this.expandedSubItem = this.getSubItemInicialExpandido(item);
      this.ajustarPosicaoFlyout();
    }
  }

  /** Ao passar o mouse em outro item, troca o flyout se ja estiver aberto */
  onItemHover(item: MenuItem, event: MouseEvent) {
    // So troca se ja tem um flyout aberto e o item tem filhos
    if (this.activeItem && item.children && this.activeItem !== item.key) {
      const el = (event.currentTarget as HTMLElement);
      const rect = el.getBoundingClientRect();
      this.flyoutTop = rect.top;
      this.activeItem = item.key;
      this.expandedSubItem = this.getSubItemInicialExpandido(item);
      this.ajustarPosicaoFlyout();
    }
  }

  /** Retorna a chave do sub-item que deve iniciar expandido (se houver) */
  private getSubItemInicialExpandido(item: MenuItem): string {
    if (!item.children) return '';
    // Se o pai marca todos os filhos como sempre expandidos, nao ha um unico "inicial"
    // (a UI deve abrir todos via isSubItemSempreExpandido). Retornamos vazio para
    // nao forcar apenas um; a lista abaixo cobre o caso "um especifico sempre expandido".
    if (item.todosFilhosSempreExpandidos) {
      return '';
    }
    const filhoSempreExpandido = item.children.find(
      child => this.subItensSempreExpandidos.includes(child.key),
    );
    return filhoSempreExpandido ? filhoSempreExpandido.key : '';
  }

  /** Retorna o pai (menu de primeiro nivel) de um sub-item, se houver */
  private getPaiDoSubItem(child: MenuItem): MenuItem | undefined {
    const buscar = (lista: MenuItem[]): MenuItem | undefined => {
      for (const item of lista) {
        if (item.children?.some(c => c.key === child.key)) return item;
      }
      return undefined;
    };
    return buscar(this.menuItems) || buscar(this.footerMenuItems);
  }

  /** Ajusta a posicao do flyout apos renderizar para nao ultrapassar a viewport */
  private ajustarPosicaoFlyout(): void {
    this.cdr.detectChanges();
    setTimeout(() => {
      const flyout = document.querySelector('.flyout-panel') as HTMLElement;
      if (!flyout) return;
      const flyoutHeight = flyout.offsetHeight;
      const margin = 8;
      const maxTop = window.innerHeight - flyoutHeight - margin;
      if (this.flyoutTop > maxTop) {
        this.flyoutTop = Math.max(margin, maxTop);
        this.cdr.detectChanges();
      }
    });
  }

  /** Fecha o flyout */
  closeFlyout() {
    this.activeItem = '';
    this.expandedSubItem = '';
  }

  /** Navega para um sub-item */
  navigateTo(child: MenuItem) {
    if (child.externalLink) {
      window.open(child.externalLink, '_blank');
      return;
    }
    if (child.action) {
      child.action();
      this.closeFlyout();
      return;
    }
    if (child.route) {
      this.selectedItem = child.key;
      this.router.navigateByUrl(child.route);
      this.closeFlyout();
    }
  }

  /** Toggle de sub-submenu */
  toggleSubItem(child: MenuItem, event: MouseEvent) {
    event.stopPropagation();
    if (!child.children) return;
    // Itens marcados como "sempre expandidos" nao podem ser recolhidos
    if (this.isSubItemSempreExpandido(child)) {
      this.expandedSubItem = child.key;
      return;
    }
    this.expandedSubItem = this.expandedSubItem === child.key ? '' : child.key;
  }

  /** Verifica se e um item de secao (titulo) */
  isSectionTitle(child: MenuItem): boolean {
    return !!child.sectionTitle;
  }

  /** Verifica se e apenas um separador visual sem titulo */
  isSeparator(child: MenuItem): boolean {
    return !!child.separator;
  }

  /** Verifica se um item tem filhos expandidos */
  hasChildren(child: MenuItem): boolean {
    return !!child.children && child.children.length > 0;
  }

  /** Verifica se o sub-item deve ficar sempre expandido (nao recolhe) */
  isSubItemSempreExpandido(child: MenuItem): boolean {
    // Regra 1: sub-item explicitamente listado
    if (this.subItensSempreExpandidos.includes(child.key)) return true;
    // Regra 2: pai marcado com flag todosFilhosSempreExpandidos
    const pai = this.getPaiDoSubItem(child);
    return !!pai?.todosFilhosSempreExpandidos;
  }

  /** Verifica se um item de menu e visivel conforme permissoes */
  isItemVisivel(item: MenuItem): boolean {
    // Titulos de secao sao sempre visiveis
    if (item.sectionTitle) return true;

    // Itens exclusivos de usuario master
    if (item.masterOnly && !this.acessoMaster) return false;

    // Itens exclusivos de usuario contador
    if (item.contadorOnly && !this.acessoContador) return false;

    // Itens exclusivos do lado CLIENTE (escondidos para a empresa contadora)
    if (item.naoContadorOnly && this.acessoContador) return false;

    // Itens que aceitam varias janelas (ex.: Relatorios): basta ter uma
    if (item.permissoes?.length) {
      return item.permissoes.some(campo => this.permissaoService.temPermissao(campo));
    }

    // Itens sem permissao definida
    if (!item.permissao) {
      // Se tem filhos, visivel se pelo menos 1 filho e visivel
      if (item.children) {
        return item.children.some(child => this.isItemVisivel(child));
      }
      return true;
    }
    return this.permissaoService.temPermissao(item.permissao);
  }

  /** Retorna filhos visiveis de um item */
  getFilhosVisiveis(item: MenuItem): MenuItem[] {
    if (!item.children) return [];
    return item.children.filter(child => this.isItemVisivel(child));
  }

}
